// =============================================================================
// Settings (§7). Open to front desk as well as admin.
//
//   Staff accounts  — ADMIN ONLY. Creating a login goes through the
//                     create-staff Edge Function (§2.6), which needs the
//                     service_role key and checks the caller is an admin.
//   Branch settings — membership fee and borrowing rules. Front desk sees and
//                     edits exactly their own branch; an admin sees them all.
//
// Front desk editing borrowing rules is a deliberate departure from §3, made
// after the first desk run: at a single-branch pilot the person who needs to
// change a loan period is the person standing at the desk. The database still
// scopes them to their own branch (0008) — this screen is not the guard.
// =============================================================================

import { el, mount, formatDate } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import { spinner } from '../lib/loading.js';
import { BUILD_ID, checkForUpdate, isUpdateReady, onUpdateFound, applyPendingUpdate }
  from '../lib/updates.js';
import { money } from '../config/school.js';
import {
  listStaff, createStaff, setStaffActive,
  allBranchSettings, updateBranchSettings, listBranches, setMembershipFee,
} from '../lib/api.js';
import { getStaff } from '../lib/auth.js';

export function settingsScreen(root) {
  let staff = [];
  let branches = [];
  let settings = [];
  let loading = true;
  let adding = false;
  let busy = false;
  // Which branch's rules are being edited. Everything is read-only until
  // "Edit" is pressed — a screenful of spinboxes invites accidental changes to
  // numbers that govern every loan in the branch.
  let editingBranch = null;
  // Shown once, after creation. Never fetched again — it is not stored anywhere
  // readable, by design.
  let newAccount = null;

  const view = el('div', { class: 'screen' });
  mount(root, view);

  async function load() {
    loading = true; render();
    try {
      [staff, branches, settings] = await Promise.all([
        listStaff(), listBranches(), allBranchSettings(),
      ]);
    } catch (err) {
      mount(view, el('p', { class: 'error' }, err.message));
      loading = false; return;
    }
    loading = false; render();
  }

  const isAdmin = () => getStaff()?.role === 'admin';

  // Front desk sees only their own branch; an admin has no home branch and
  // manages all of them.
  const visibleSettings = () => {
    const mine = getStaff()?.branch_id;
    return mine ? settings.filter((s) => s.branch_id === mine) : settings;
  };

  function render() {
    mount(view,
      el('h1', { class: 'screen-title' }, T.settingsTitle),
      whoAmI(),
      loading ? spinner() : null,
      // Staff accounts stay admin-only — front desk cannot create logins.
      !loading && isAdmin() ? staffSection() : null,
      !loading ? rulesSection() : null,
      versionSection(),
    );
  }

  // --- Version -------------------------------------------------------------
  //
  // Two questions this answers that nothing else could: "which version is this
  // desk on?" — previously unanswerable over the phone — and "I've been told
  // it's fixed but I can't see it", which had no remedy short of clearing a
  // browser cache. A browser decides for itself when to look for a new service
  // worker, and Safari can take hours.
  function versionSection() {
    const status = el('p', { class: 'muted' }, '');
    const btn = el('button', { class: 'btn btn-sm', type: 'button' });

    function paint() {
      if (isUpdateReady()) {
        status.textContent = T.updateAvailable;
        btn.textContent = T.updateNow;
        btn.onclick = () => { btn.disabled = true; btn.textContent = T.updating; applyPendingUpdate(); };
      } else {
        btn.textContent = T.checkForUpdates;
        btn.onclick = async () => {
          btn.disabled = true;
          status.textContent = T.checkingForUpdates;
          const found = await checkForUpdate();
          btn.disabled = false;
          // onUpdateFound repaints if one turned up; this covers the other case,
          // where saying nothing would look like the button did nothing.
          if (!found && !isUpdateReady()) status.textContent = T.upToDate;
        };
      }
    }
    onUpdateFound(paint);
    paint();

    return el('section', { class: 'card' },
      el('h2', { class: 'card-title' }, T.appVersion),
      el('dl', { class: 'detail' },
        el('dt', {}, T.versionLabel), el('dd', { class: 'basket-code' }, BUILD_ID),
      ),
      status,
      el('div', { class: 'action-row' }, btn),
    );
  }

  // "Which branch am I?" — asked for explicitly. Front desk staff at three
  // branches all see the same screen otherwise, and the numbers they are
  // editing only apply to theirs.
  function whoAmI() {
    const me = getStaff();
    if (!me) return null;
    const branch = branchName(me.branch_id);
    return el('div', { class: 'whoami' },
      el('span', { class: 'pill' }, me.role === 'admin' ? T.roleAdmin : T.roleFrontDesk),
      el('span', {}, branch ? T.signedInAt(branch) : T.signedInAllBranches),
    );
  }

  // --- Staff ---------------------------------------------------------------

  function staffSection() {
    return el('section', { class: 'card' },
      el('div', { class: 'session-head' },
        el('h2', { class: 'card-title' }, T.staffAccounts),
        !adding
          ? el('button', { class: 'btn btn-primary', onClick: () => { adding = true; render(); } }, T.addStaff)
          : null,
      ),

      newAccount ? credentialsCard() : null,
      adding ? staffForm() : null,

      ...staff.map((s) => el('div', { class: 'loan-row' },
        el('span', { class: 'loan-title' }, s.full_name),
        el('span', { class: 'muted' }, s.email),
        el('span', { class: 'pill' }, s.role === 'admin' ? T.roleAdmin : T.roleFrontDesk),
        el('span', { class: 'muted' }, branchName(s.branch_id) ?? T.allBranches),
        !s.is_active ? el('span', { class: 'pill pill-warn' }, T.inactive) : null,
        // An admin must not be able to lock themselves out.
        s.id === getStaff()?.id
          ? el('span', { class: 'muted' }, T.thisIsYou)
          : el('button', {
              class: 'btn btn-sm', disabled: busy,
              onClick: () => toggleActive(s),
            }, s.is_active ? T.deactivate : T.reactivate),
      )),
    );
  }

  const branchName = (id) => branches.find((b) => b.id === id)?.name ?? null;

  // The temporary password is shown ONCE. Say so plainly — an admin who closes
  // this without copying it has to reset the password in the dashboard.
  function credentialsCard() {
    return el('section', { class: 'card card-warn' },
      el('h2', { class: 'card-title' }, T.accountCreated(newAccount.staff.full_name)),
      el('p', { class: 'muted' }, T.passwordShownOnce),
      el('dl', { class: 'detail' },
        el('dt', {}, T.email), el('dd', {}, newAccount.staff.email),
        el('dt', {}, T.tempPassword),
        el('dd', {}, el('code', { class: 'temp-password' }, newAccount.temporary_password)),
      ),
      // Wrapped, because el() appends siblings with no whitespace node between
      // them — two inline-flex buttons were meeting edge to edge.
      el('div', { class: 'form-actions' },
        el('button', {
          class: 'btn',
          onClick: async () => {
            try {
              await navigator.clipboard.writeText(
                `${newAccount.staff.email}\n${newAccount.temporary_password}`);
              flash(T.copied, 'notice');
            } catch { flash(T.copyFailed); }
          },
        }, T.copyDetails),
        el('button', { class: 'btn', onClick: () => { newAccount = null; render(); } }, T.done),
      ),
    );
  }

  function staffForm() {
    const name = el('input', { type: 'text', class: 'text-input', required: true,
      placeholder: T.staffName, 'aria-label': T.staffName, autocomplete: 'off' });
    const email = el('input', { type: 'email', class: 'text-input', required: true,
      placeholder: T.email, 'aria-label': T.email, autocomplete: 'off' });
    const role = el('select', { class: 'text-input', 'aria-label': T.role },
      el('option', { value: 'front_desk' }, T.roleFrontDesk),
      el('option', { value: 'admin' }, T.roleAdmin));
    const branch = el('select', { class: 'text-input', 'aria-label': T.branch },
      ...branches.map((b) => el('option', { value: b.id }, b.name)));
    const branchField = el('div', { class: 'field' },
      el('label', { class: 'field-label' }, T.branch), branch);

    // An admin has no home branch (§4) — hide the picker rather than let
    // someone submit a combination the database will reject.
    const syncRole = () => branchField.classList.toggle('hidden', role.value === 'admin');
    role.addEventListener('change', syncRole);
    queueMicrotask(syncRole);

    async function submit(e) {
      e.preventDefault();
      if (busy) return;
      busy = true; render();
      try {
        newAccount = await createStaff({
          full_name: name.value.trim(),
          email: email.value.trim(),
          role: role.value,
          branch_id: role.value === 'admin' ? null : branch.value,
        });
        adding = false; busy = false;
        await load();
      } catch (err) {
        busy = false; render();
        flash(err.message);
      }
    }

    return el('form', { class: 'card card-warn', onSubmit: submit },
      el('h3', { class: 'card-title' }, T.newStaff),
      el('p', { class: 'muted' }, T.newStaffHint),
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.staffName), name),
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.email), email),
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.role), role),
      branchField,
      el('div', { class: 'form-actions' },
        el('button', { class: 'btn btn-primary', type: 'submit', disabled: busy },
          busy ? T.creating : T.createAccount),
        el('button', { class: 'btn', onClick: () => { adding = false; render(); } }, T.cancel),
      ),
    );
  }

  async function toggleActive(s) {
    busy = true; render();
    try {
      await setStaffActive(s.id, !s.is_active);
      busy = false;
      await load();
    } catch (err) {
      busy = false; render();
      flash(err.message);
    }
  }

  // --- Borrowing rules -----------------------------------------------------

  function rulesSection() {
    const rows = visibleSettings();
    return el('section', { class: 'card' },
      el('h2', { class: 'card-title' }, T.branchSettingsTitle),
      el('p', { class: 'muted' }, T.rulesHint),
      rows.length ? null : el('p', { class: 'empty' }, T.noBranchSettings),
      ...rows.map(rulesForm),
    );
  }

  function rulesForm(s) {
    const editing = editingBranch === s.branch_id;

    const heading = el('div', { class: 'rules-head' },
      el('h3', { class: 'rules-branch' }, s.name ?? s.code),
      editing
        ? null
        : el('button', {
            class: 'btn btn-sm',
            onClick: () => { editingBranch = s.branch_id; render(); },
          }, T.edit),
    );

    // Read-only by default: plain values, no spinboxes.
    if (!editing) {
      return el('div', { class: 'rules-form' }, heading,
        el('dl', { class: 'detail' },
          el('dt', {}, T.membershipFee), el('dd', {}, money(s.membership_fee_amount)),
          el('dt', {}, T.maxLoans), el('dd', {}, String(s.max_concurrent_loans)),
          el('dt', {}, T.loanPeriod), el('dd', {}, T.days(s.loan_period_days)),
          el('dt', {}, T.renewalsAllowed), el('dd', {}, String(s.renewals_allowed)),
        ));
    }

    const num = (value, label, min = 0) => el('input', {
      type: 'number', class: 'text-input', min: String(min), value: String(value ?? ''),
      inputmode: 'numeric', 'aria-label': label,
    });
    const fee = num(s.membership_fee_amount, T.membershipFee, 0);
    const maxLoans = num(s.max_concurrent_loans, T.maxLoans, 1);
    const period = num(s.loan_period_days, T.loanPeriod, 1);
    const renewals = num(s.renewals_allowed, T.renewalsAllowed, 0);

    async function save(e) {
      e.preventDefault();
      if (busy) return;
      busy = true; render();
      try {
        // The fee goes through its own function (front desk may set it too);
        // the borrowing rules are a plain admin-only table update.
        await setMembershipFee(s.branch_id, Number(fee.value));
        await updateBranchSettings(s.branch_id, {
          max_concurrent_loans: Number(maxLoans.value),
          loan_period_days: Number(period.value),
          renewals_allowed: Number(renewals.value),
        });
        busy = false; editingBranch = null;
        await load();
        flash(T.rulesSaved(s.name ?? s.code), 'notice');
      } catch (err) {
        busy = false; render();
        flash(err.message);
      }
    }

    return el('form', { class: 'rules-form rules-editing', onSubmit: save }, heading,
      el('div', { class: 'rules-grid' },
        el('label', { class: 'field-label' }, T.membershipFee), fee,
        el('label', { class: 'field-label' }, T.maxLoans), maxLoans,
        el('label', { class: 'field-label' }, T.loanPeriod), period,
        el('label', { class: 'field-label' }, T.renewalsAllowed), renewals,
      ),
      el('p', { class: 'muted' }, T.feeChangeHint),
      el('div', { class: 'form-actions' },
        el('button', { class: 'btn btn-primary', type: 'submit', disabled: busy },
          busy ? T.saving : T.saveRules),
        el('button', { class: 'btn', onClick: () => { editingBranch = null; render(); } }, T.cancel),
      ),
    );
  }

  function flash(message, kind = 'error') {
    const note = el('p', { class: kind === 'error' ? 'error' : 'notice', role: 'alert' }, message);
    view.querySelector('.screen-title')?.after(note);
    setTimeout(() => note.remove(), 6000);
  }

  load();
  return { destroy: () => {} };
}
